import { useMemo, useState } from 'react'
import { HardDrive, Video } from 'lucide-react'
import { Shell, useShellMeta } from '../../../admin/Shell.js'
import { TextInput } from '../../../admin/form.js'
import { fmtAge, fmtHeure, fmtNombre, fmtRatio, Pastille, PastilleEtat, useVueSonde, type Ton } from '../commun.js'
import {
  Bandeaux, donneeDe, EnTeteDomaine, Journal, LienAppareil, Lu, Panneau, Sources, Tuile, Tuiles, useADecider, useEvenements, usePeutDetails, Vide, Zone,
} from './kit.js'
import { agePlusAncien, bool01, compterADecider, fmtCapacite, fmtDebitOctets, fmtDecimal, type Generique, type SourceDomaine } from './kit-modele.js'
import {
  camerasParSalle, estVideoDoc, etatDisque, etatFlux, nvrDuRegistre, nvrsMesures, protectMesure, sourceDuNvr, tonVoie, verdictStockage, voiesEnEvidence,
  type NvrMesure, type NvrRegistre, type ProtectMesure, type VideoDoc,
} from './video-modele.js'
import './video.css'

/**
 * SÉCURITÉ › VIDÉOSURVEILLANCE (21/10/2026) — `GET /v1/video` de Sonde.
 *
 * En tête : les voies qui n'enregistrent plus. Puis les enregistreurs (NVR
 * Hikvision, Protect) avec leur RÉTENTION EFFECTIVE — un enregistreur plein
 * qui écrase ses plus anciennes images est normal —, leurs disques, les
 * caméras du registre, les flux MediaMTX, le journal et les sources.
 * Une source indisponible est grise, jamais « hors ligne ». Aucun conseil.
 */

const KINDS = [
  'CAMERA_RECORDING_STOPPED', 'CAMERA_RECORDING_RESUMED', 'NVR_RECORD_FAULT', 'NVR_VIDEOLOSS', 'NVR_CAMERA_DISCONNECTED',
  'NVR_CHANNEL_BITRATE_DROP', 'NVR_HDD_ERROR', 'NVR_HDD_FULL', 'NVR_HDD_SMART', 'NVR_HDD_WARNING', 'NVR_HDD_RECOVERED',
  'NVR_DEVICE_FAULT', 'NVR_NETWORK_DOWN', 'NVR_POE_EXCEPTION', 'NVR_VIDEO_EXCEPTION', 'CHANNEL_DEVICE_MISMATCH',
]

const VOIES_VISIBLES = 12
const CAMERAS_VISIBLES = 120

export function VideoPage() {
  const meta = useShellMeta()
  const details = usePeutDetails()
  const { lecture, precedente } = useVueSonde('video', estVideoDoc)
  const { d, perime } = donneeDe(lecture, precedente)
  const ev = useEvenements(KINDS, 20)
  const decouvertes = useADecider(null)

  const mesures = useMemo(() => (d ? nvrsMesures(d.generic) : []), [d])
  const protect = useMemo(() => (d ? protectMesure(d.generic) : null), [d])
  const evidence = useMemo(() => (d ? voiesEnEvidence(d.nvrs, mesures) : []), [d, mesures])

  return (
    <Shell active="securite" meta={meta}>
      <main className="lc-page lc-page--wide lc-stagger sv-dom-page sv-vid">
        <EnTeteDomaine
          retour={{ href: '/securite', label: 'Sécurité' }}
          icone={<Video size={26} />}
          titre="Vidéosurveillance"
          sousTitre="Les caméras, ce que les enregistreurs gardent vraiment, et les voies qui n’enregistrent plus. Lu sur Sonde, sans rien modifier."
          genereA={d?.generated_at}
          perime={perime}
        />
        <Bandeaux lecture={lecture} genereA={d?.generated_at} collector={d?.collector} />
        {!d ? (lecture ? null : <p className="lc-hint">Lecture en cours…</p>) : (
          <Zone perime={perime}>
            <Chiffres d={d} mesures={mesures} protect={protect} />
            <EnEvidence evidence={evidence} observe={d.nvrs.some((n) => n.channels.length > 0) || mesures.some((m) => m.voies.length > 0)} />
            <Enregistreurs d={d} mesures={mesures} protect={protect} details={details} />
            <Cameras d={d} protect={protect} mesures={mesures} aDecider={compterADecider(decouvertes, ['camera_'])} nvrADecider={compterADecider(decouvertes, ['nvr_'])} />
            <div className="sv-dom-colonnes">
              <Flux d={d} details={details} />
              <SwitchsVideo generic={d.generic} details={details} />
            </div>
            <div className="sv-dom-colonnes">
              <Panneau id="sv-vid-journal" titre="Journal vidéo" indice="dernières 24 h, message de Sonde">
                <Journal lecture={ev.lecture} precedente={ev.precedente} vide="Aucun événement vidéo dans les dernières 24 heures." />
              </Panneau>
              <Panneau id="sv-vid-sources" titre="Sources" indice="d’où viennent ces chiffres">
                <Sources sources={d.sources} details={details} />
              </Panneau>
            </div>
          </Zone>
        )}
      </main>
    </Shell>
  )
}

/* ─────────────────────────────── Chiffres d'en-tête */

function Chiffres({ d, mesures, protect }: { d: VideoDoc; mesures: NvrMesure[]; protect: ProtectMesure | null }) {
  const c = d.counts
  const src = (code: string) => d.sources.find((s) => s.code === code)
  const ageRegistre = agePlusAncien(d.cameras.map((x) => x.state.data_age_s ?? null))

  const tonCam: Ton = c.cameras === 0 ? 'gris' : c.offline > 0 ? 'err' : c.unobserved > 0 ? 'warn' : 'ok'

  // Voies : le registre d'abord ; sinon ce que les NVR mesurent d'eux-mêmes.
  let voies: { valeur: string; ton: Ton; detail: string; source: string; age: number | null }
  if (d.nvrs.length > 0 && c.channels > 0) {
    voies = {
      valeur: fmtRatio(c.channels_recording, c.channels),
      ton: c.channels_recording < c.channels ? 'err' : 'ok',
      detail: c.channels_recording < c.channels ? `${c.channels - c.channels_recording} voie${c.channels - c.channels_recording > 1 ? 's' : ''} n’enregistre${c.channels - c.channels_recording > 1 ? 'nt' : ''} plus` : 'toutes les voies observées enregistrent',
      source: 'NVR au registre', age: agePlusAncien(d.nvrs.flatMap((n) => n.channels.map((x) => x.data_age_s))),
    }
  } else {
    const toutes = mesures.flatMap((m) => m.voies)
    const continues = toutes.filter((v) => v.continu === true && v.planning !== false).length
    const coupees = toutes.filter((v) => v.planning === false).length
    voies = toutes.length === 0
      ? { valeur: '—', ton: 'gris', detail: 'aucune voie mesurée', source: 'NVR Hikvision', age: null }
      : {
          valeur: fmtRatio(continues, toutes.length), ton: coupees > 0 ? 'warn' : 'ok',
          detail: coupees > 0 ? `${coupees} planning${coupees > 1 ? 's' : ''} d’enregistrement désactivé${coupees > 1 ? 's' : ''}` : 'enregistrement continu 7 j/7',
          source: 'NVR Hikvision (mesuré)', age: agePlusAncien(toutes.map((v) => v.age_s)),
        }
  }

  const prets = d.streams.filter((s) => s.ready === true).length
  const pasPrets = d.streams.filter((s) => s.ready === false && s.on_demand !== true).length
  const retentions = [...mesures.map((m) => m.retention_j), protect?.retention_j ?? null].filter((x): x is number => typeof x === 'number')

  return (
    <Tuiles>
      <Tuile libelle="Caméras suivies" valeur={fmtNombre(c.cameras)} ton={tonCam}
             detail={c.cameras === 0 ? 'aucune au registre : à valider' : `${c.online} en ligne · ${c.offline} hors ligne${c.unobserved ? ` · ${c.unobserved} non observée${c.unobserved > 1 ? 's' : ''}` : ''}`}
             source="Registre de Sonde" age={ageRegistre ?? src('unifi_protect')?.data_age_s ?? null} />
      <Tuile libelle="Voies qui enregistrent" valeur={voies.valeur} ton={voies.ton} detail={voies.detail} source={voies.source} age={voies.age} />
      <Tuile libelle="Caméras vues par Protect" valeur={fmtRatio(protect?.cameras_connectees, protect?.cameras_total)}
             ton={protect?.cameras_total == null || protect.cameras_connectees == null ? 'gris' : protect.cameras_connectees < protect.cameras_total ? 'warn' : 'ok'}
             detail="connectées / connues" source="UniFi Protect" age={protect?.age_s ?? null} />
      <Tuile libelle="Viewports" valeur={fmtRatio(protect?.viewports_connectes, protect?.viewports_total)}
             ton={protect?.viewports_total == null || protect.viewports_connectes == null ? 'gris' : protect.viewports_connectes < protect.viewports_total ? 'warn' : 'ok'}
             detail="écrans connectés / connus" source="UniFi Protect" age={protect?.age_s ?? null} />
      <Tuile libelle="Flux MediaMTX prêts" valeur={d.streams.length ? fmtRatio(prets, d.streams.length) : '—'}
             ton={d.streams.length === 0 ? 'gris' : pasPrets > 0 ? 'warn' : 'ok'}
             detail={d.streams.length === 0 ? 'aucun flux lu' : pasPrets > 0 ? `${pasPrets} flux pas prêt${pasPrets > 1 ? 's' : ''}` : 'relais vidéo en service'}
             source="MediaMTX" age={agePlusAncien(d.streams.map((s) => s.data_age_s))} />
      <Tuile libelle="Rétention effective la plus courte" valeur={retentions.length ? fmtDecimal(Math.min(...retentions), 'j', 0) : '—'}
             ton={retentions.length ? 'neutre' : 'gris'}
             detail={retentions.length ? 'images les plus anciennes encore gardées' : 'pas encore mesurée par les enregistreurs'}
             source="NVR et Protect" age={agePlusAncien([...mesures.map((m) => m.age_s), protect?.age_s ?? null])} />
    </Tuiles>
  )
}

/* ─────────────────────────────── Voies à voir en premier */

function EnEvidence({ evidence, observe }: { evidence: ReturnType<typeof voiesEnEvidence>; observe: boolean }) {
  const [tout, setTout] = useState(false)
  const graves = evidence.filter((v) => v.ton === 'err').length
  const visibles = tout ? evidence : evidence.slice(0, VOIES_VISIBLES)
  return (
    <Panneau id="sv-vid-evidence" titre="Voies à voir en premier"
             className={graves > 0 ? 'sv-vid-evidence--err' : undefined}
             indice={evidence.length ? `${evidence.length} voie${evidence.length > 1 ? 's' : ''}${graves ? `, dont ${graves} qui n’enregistre${graves > 1 ? 'nt' : ''} plus` : ''}` : undefined}>
      {evidence.length === 0 ? (
        observe
          ? <p className="sv-vid-calme"><Pastille ton="ok">Toutes les voies observées enregistrent</Pastille></p>
          : <p className="lc-hint">Aucune voie n’est encore observée : rien à signaler ne veut pas dire que tout enregistre.</p>
      ) : (
        <>
          <ul className="sv-dom-lignes">
            {visibles.map((v) => (
              <li key={v.cle} className={`sv-dom-ligne sv-dom-ligne--${v.ton}`}>
                <span className="sv-dom-ligne__nom">
                  {v.nvr} · voie D{v.voie}{v.camera ? <> · <LienAppareil uid={v.camera.uid} nom={v.camera.nom} /></> : null}
                </span>
                <Pastille ton={v.ton}>{v.ton === 'err' ? 'N’enregistre plus' : 'À vérifier'}</Pastille>
                <span className="sv-dom-ligne__meta">{v.texte} <Lu age={v.age_s} /></span>
              </li>
            ))}
          </ul>
          {evidence.length > VOIES_VISIBLES ? (
            <button type="button" className="lc-btn lc-btn--ghost sv-dom-plus" onClick={() => setTout((x) => !x)}>
              {tout ? 'Réduire' : `Afficher les ${evidence.length - VOIES_VISIBLES} autres`}
            </button>
          ) : null}
        </>
      )}
    </Panneau>
  )
}

/* ─────────────────────────────── Enregistreurs */

function Enregistreurs({ d, mesures, protect, details }: { d: VideoDoc; mesures: NvrMesure[]; protect: ProtectMesure | null; details: boolean }) {
  // Une carte par NVR : celui du registre (avec ses voies) et/ou ses mesures.
  const cartes: { cle: string; registre: NvrRegistre | null; mesure: NvrMesure | null }[] = []
  const prises = new Set<NvrMesure>()
  for (const n of d.nvrs) {
    const m = mesures.find((x) => nvrDuRegistre(x.cle, [n]) === n) ?? null
    if (m) prises.add(m)
    cartes.push({ cle: n.device.uid, registre: n, mesure: m })
  }
  for (const m of mesures) if (!prises.has(m)) cartes.push({ cle: m.cle, registre: null, mesure: m })

  const protectSource = d.sources.find((s) => s.code === 'unifi_protect') ?? null
  const rien = cartes.length === 0 && !protect

  return (
    <Panneau id="sv-vid-nvr" titre="Enregistreurs" indice="rétention effective, voies, disques">
      <p className="sv-vid-regle"><HardDrive size={16} aria-hidden="true" /> Un enregistreur plein qui écrase ses plus anciennes images est normal : ce qui compte, c’est combien de jours d’images il garde vraiment.</p>
      {rien ? (
        <Vide titre="Aucun enregistreur lu pour l’instant." versADecider>
          <p>Ni NVR au registre de Sonde, ni mesure des NVR Hikvision ou de Protect. Les NVR découverts attendent dans « À valider ».</p>
        </Vide>
      ) : (
        <div className="sv-vid-cartes">
          {cartes.map((c) => <CarteNvr key={c.cle} registre={c.registre} mesure={c.mesure} sources={d.sources} details={details} />)}
          {protect ? <CarteProtect p={protect} source={protectSource} /> : null}
        </div>
      )}
    </Panneau>
  )
}

function CarteNvr({ registre, mesure, sources, details }: { registre: NvrRegistre | null; mesure: NvrMesure | null; sources: SourceDomaine[]; details: boolean }) {
  const source = mesure ? sourceDuNvr(mesure.cle, sources) : null
  const indispo = source !== null && source.status !== 'OK' && source.status !== 'DEGRADED'
  const nom = registre?.device.display_name ?? mesure?.cle.toUpperCase() ?? '—'
  const voiesReg = registre?.channels ?? []
  const voiesMes = mesure?.voies ?? []
  const nbVoies = voiesReg.length || voiesMes.length
  const enregistrent = voiesReg.length
    ? voiesReg.filter((v) => v.recording === true).length
    : voiesMes.filter((v) => v.continu === true && v.planning !== false).length
  const age = agePlusAncien([mesure?.age_s ?? null, ...voiesReg.map((v) => v.data_age_s)])

  return (
    <article className={`sv-vid-carte${indispo ? ' sv-vid-carte--gris' : ''}`}>
      <header className="sv-vid-carte__tete">
        <span className="sv-vid-carte__nom">{registre ? <LienAppareil uid={registre.device.uid} nom={nom} /> : nom}</span>
        {registre
          ? <PastilleEtat etat={registre.device.state.display_state} libelle={registre.device.state.display_label} />
          : source ? <Pastille ton={indispo ? 'gris' : source.status === 'DEGRADED' ? 'warn' : 'ok'}>{indispo ? 'Source indisponible' : source.status === 'DEGRADED' ? 'Lecture dégradée' : 'Lu par Sonde'}</Pastille>
          : <Pastille ton="gris">Pas au registre</Pastille>}
      </header>
      {indispo ? <p className="sv-vid-carte__note">Source indisponible{source?.since ? ` depuis ${fmtHeure(source.since)}` : ''} : dernières données{age !== null ? ` d’il y a ${fmtAge(age).replace(/^il y a /, '')}` : ''}, affichées en gris. Ce n’est pas une panne de l’enregistreur.</p> : null}
      {!registre ? <p className="sv-vid-carte__note">Lu directement sur l’enregistreur ; pas encore rattaché à un poste du registre.</p> : null}
      <dl className="sv-dom-faits">
        <div><dt>Rétention effective</dt><dd>{fmtDecimal(mesure?.retention_j, 'j', 0)}</dd></div>
        <div><dt>Voies</dt><dd>{nbVoies ? `${enregistrent}/${nbVoies} enregistrent` : '—'}</dd></div>
        {mesure?.couverture_pct != null ? <div><dt>Voies à rétention mesurée</dt><dd>{fmtDecimal(mesure.couverture_pct, '%', 0)}</dd></div> : null}
        <div><dt>Débit déclaré</dt><dd>{fmtDecimal(mesure?.debit_mbps, 'Mbit/s')}</dd></div>
        <div><dt>Écart d’horloge</dt><dd>{fmtDecimal(mesure?.horloge_s, 's', 0)}</dd></div>
        {registre ? <div><dt>Mémoire libre</dt><dd>{fmtDecimal(registre.free_ram_mb, 'Mo', 0)}</dd></div> : null}
        {registre ? <div><dt>Temps de réponse</dt><dd>{fmtDecimal(registre.resp_ms, 'ms', 0)}</dd></div> : null}
      </dl>
      {nbVoies ? (
        <div className="sv-vid-voies" role="list" aria-label={`Voies de ${nom}`}>
          {voiesReg.length
            ? voiesReg.map((v) => (
              <span key={v.channel} role="listitem" className={`sv-vid-voie sv-vid-voie--${tonVoie(v)}`}
                    title={`D${v.channel}${v.camera ? ` · ${v.camera.display_name}` : ''} · ${v.recording === true ? 'enregistre' : v.recording === false ? 'n’enregistre plus' : 'inconnu'}`}>
                D{v.channel}
              </span>
            ))
            : voiesMes.map((v) => {
              const ton: Ton = v.planning === false ? 'warn' : v.continu === true ? 'ok' : v.continu === false ? 'warn' : 'gris'
              return (
                <span key={v.voie} role="listitem" className={`sv-vid-voie sv-vid-voie--${ton}`}
                      title={`D${v.voie} · ${v.planning === false ? 'planning désactivé' : v.continu === true ? 'enregistrement continu' : v.continu === false ? 'pas continu' : 'inconnu'}${v.retention_j != null ? ` · ${v.retention_j} j` : ''}`}>
                  D{v.voie}
                </span>
              )
            })}
        </div>
      ) : null}
      {mesure && mesure.disques.length ? (
        <ul className="sv-vid-disques">
          {mesure.disques.map((k) => {
            const e = etatDisque(k)
            const secteurs = [k.realloues, k.en_attente, k.non_corrigibles]
            return (
              <li key={k.disque} className="sv-vid-disque">
                <span className="sv-vid-disque__nom">Disque {k.disque.replace(/^hdd/i, '')}{details ? <span className="sv-dom-mono"> {k.disque}</span> : null}</span>
                <Pastille ton={e.ton}>{e.libelle}</Pastille>
                <span className="sv-vid-disque__meta">
                  {fmtCapacite(k.capacite_go)}
                  {k.temp_c != null ? ` · ${fmtDecimal(k.temp_c, '°C', 0)}` : ''}
                  {k.smart_ok != null ? ` · SMART ${k.smart_ok ? 'sain' : 'dégradé'}` : ''}
                  {secteurs.some((s) => s != null) ? ` · secteurs ${secteurs.map((s) => fmtNombre(s)).join(' / ')}` : ''}
                  {k.jours != null ? ` · ${fmtNombre(k.jours)} j de service` : ''}
                </span>
              </li>
            )
          })}
        </ul>
      ) : null}
      <p className="sv-vid-carte__src"><Lu age={age} source={source?.label ?? 'Registre de Sonde'} /></p>
    </article>
  )
}

function CarteProtect({ p, source }: { p: ProtectMesure; source: SourceDomaine | null }) {
  const st = verdictStockage(p.stockage_pct, p.ecrasement)
  const indispo = source !== null && source.status !== 'OK' && source.status !== 'DEGRADED'
  const sousCible = p.retention_j !== null && p.retention_cible_j !== null && p.retention_j < p.retention_cible_j
  return (
    <article className={`sv-vid-carte${indispo ? ' sv-vid-carte--gris' : ''}`}>
      <header className="sv-vid-carte__tete">
        <span className="sv-vid-carte__nom">UniFi Protect</span>
        <Pastille ton={indispo ? 'gris' : st.ton}>{indispo ? 'Source indisponible' : st.ton === 'ok' ? 'Enregistre' : st.ton === 'err' ? 'N’enregistre plus' : 'Stockage non lu'}</Pastille>
      </header>
      <p className="sv-vid-carte__note">{st.texte}</p>
      <dl className="sv-dom-faits">
        <div><dt>Rétention effective</dt><dd>{fmtDecimal(p.retention_j, 'j', 0)}</dd></div>
        <div><dt>Rétention demandée</dt><dd>{fmtDecimal(p.retention_cible_j, 'j', 0)}</dd></div>
        <div><dt>Capacité estimée</dt><dd>{fmtDecimal(p.capacite_j, 'j', 0)}</dd></div>
        <div><dt>Stockage</dt><dd>{p.stockage_utilise_go != null || p.stockage_total_go != null ? `${fmtCapacite(p.stockage_utilise_go)} / ${fmtCapacite(p.stockage_total_go)}` : '—'}</dd></div>
        <div><dt>Caméras tierces</dt><dd>{fmtNombre(p.cameras_tierces)}</dd></div>
      </dl>
      {sousCible ? <p className="sv-vid-carte__note sv-vid-carte__note--warn">La rétention effective est en dessous de la rétention demandée dans Protect.</p> : null}
      {p.absents_du_registre ? <p className="sv-vid-carte__note sv-vid-carte__note--warn">{p.absents_du_registre} poste{p.absents_du_registre > 1 ? 's' : ''} du registre introuvable{p.absents_du_registre > 1 ? 's' : ''} dans Protect.</p> : null}
      <p className="sv-vid-carte__src"><Lu age={p.age_s} source={source?.label ?? 'UniFi Protect'} /></p>
    </article>
  )
}

/* ─────────────────────────────── Caméras du registre */

function Cameras({ d, protect, mesures, aDecider, nvrADecider }: { d: VideoDoc; protect: ProtectMesure | null; mesures: NvrMesure[]; aDecider: number | null; nvrADecider: number | null }) {
  const [filtre, setFiltre] = useState('')
  const [tout, setTout] = useState(false)
  const groupes = useMemo(() => camerasParSalle(d.cameras, filtre), [d.cameras, filtre])
  const voiesMesurees = mesures.reduce((n, m) => n + m.voies.length, 0)

  if (d.cameras.length === 0) {
    return (
      <Panneau id="sv-vid-cameras" titre="Caméras" indice="registre de Sonde">
        <Vide titre="Aucune caméra n’est encore rattachée au registre de Sonde." versADecider>
          <p>
            Sonde les a découvertes mais attend qu’on les valide une à une{aDecider ? ` : ${aDecider} caméra${aDecider > 1 ? 's' : ''} proposée${aDecider > 1 ? 's' : ''}` : ''}
            {nvrADecider ? ` et ${nvrADecider} enregistreur${nvrADecider > 1 ? 's' : ''}` : ''}{aDecider || nvrADecider ? ' attendent dans « À valider ».' : '.'}
          </p>
          <p>
            En attendant, l’état de chaque caméra n’est pas suivi ; ce que les enregistreurs disent d’eux-mêmes reste lu
            {protect?.cameras_total != null ? ` (Protect connaît ${protect.cameras_total} caméra${protect.cameras_total > 1 ? 's' : ''}` : ''}
            {protect?.cameras_total != null && voiesMesurees ? `, les NVR Hikvision ${voiesMesurees} voies)` : protect?.cameras_total != null ? ')' : voiesMesurees ? ` (les NVR Hikvision exposent ${voiesMesurees} voies)` : ''}.
          </p>
        </Vide>
      </Panneau>
    )
  }

  let reste = tout ? Number.POSITIVE_INFINITY : CAMERAS_VISIBLES
  const total = groupes.reduce((n, g) => n + g.cameras.length, 0)
  return (
    <Panneau id="sv-vid-cameras" titre="Caméras" indice={`${d.cameras.length} au registre de Sonde`}
             droite={d.cameras.length > 12 ? <span className="sv-vid-filtre"><TextInput value={filtre} onChange={setFiltre} placeholder="Chercher une caméra" ariaLabel="Chercher une caméra" /></span> : null}>
      {total === 0 ? <p className="lc-hint">Aucune caméra ne correspond à « {filtre} ».</p> : (
        <div className="sv-vid-salles">
          {groupes.map((g) => {
            if (reste <= 0) return null
            const visibles = g.cameras.slice(0, reste)
            reste -= visibles.length
            return (
              <section key={g.salle} className="sv-vid-salle" aria-label={g.salle}>
                <h3 className="sv-vid-salle__titre">{g.salle} <span className="lc-hint">{g.cameras.length}</span></h3>
                <ul className="sv-dom-lignes">
                  {visibles.map((c) => (
                    <li key={c.uid} className={`sv-dom-ligne${c.state.display_state === 'OFFLINE' ? ' sv-dom-ligne--err' : ''}`}>
                      <span className="sv-dom-ligne__nom"><LienAppareil uid={c.uid} nom={c.display_name} zone={c.zone_code} /></span>
                      <PastilleEtat etat={c.state.display_state} libelle={c.state.display_label} />
                      <span className="sv-dom-ligne__meta">
                        {c.state.cause_label && c.state.cause !== 'SELF' ? `${c.state.cause_label} · ` : ''}
                        <Lu age={c.state.data_age_s} />
                      </span>
                    </li>
                  ))}
                </ul>
              </section>
            )
          })}
        </div>
      )}
      {total > CAMERAS_VISIBLES ? (
        <button type="button" className="lc-btn lc-btn--ghost sv-dom-plus" onClick={() => setTout((x) => !x)}>
          {tout ? 'Réduire' : `Afficher les ${total - CAMERAS_VISIBLES} autres caméras`}
        </button>
      ) : null}
    </Panneau>
  )
}

/* ─────────────────────────────── Flux MediaMTX */

function Flux({ d, details }: { d: VideoDoc; details: boolean }) {
  const [tout, setTout] = useState(false)
  const rang: Record<Ton, number> = { err: 0, warn: 1, gris: 2, maint: 3, off: 4, ok: 5 }
  const flux = [...d.streams].sort((a, z) => rang[etatFlux(a).ton] - rang[etatFlux(z).ton] || a.path.localeCompare(z.path))
  const visibles = tout ? flux : flux.slice(0, 10)
  const source = d.sources.find((s) => s.code === 'mediamtx') ?? null
  return (
    <Panneau id="sv-vid-flux" titre="Flux MediaMTX" indice={`${d.streams.length} flux relayés`}>
      {flux.length === 0 ? (
        <p className="lc-hint">Aucun flux lu sur MediaMTX{source && source.status !== 'OK' ? ` : source ${source.status_label ?? source.status}` : ''}.</p>
      ) : (
        <>
          <ul className="sv-dom-lignes">
            {visibles.map((f) => {
              const e = etatFlux(f)
              return (
                <li key={f.path} className={`sv-dom-ligne${e.ton === 'warn' ? ' sv-dom-ligne--warn' : ''}`}>
                  <span className="sv-dom-ligne__nom">{f.uid ? <LienAppareil uid={f.uid} nom={f.path} /> : <span className="sv-dom-mono">{f.path}</span>}</span>
                  <Pastille ton={e.ton}>{e.libelle}</Pastille>
                  <span className="sv-dom-ligne__meta">
                    {f.readers != null ? `${f.readers} lecteur${f.readers > 1 ? 's' : ''} · ` : ''}entrée {fmtDebitOctets(f.bytes_in_bps)}
                    {f.frames_err_5m ? ` · ${f.frames_err_5m} image${f.frames_err_5m > 1 ? 's' : ''} en erreur (5 min)` : ''}
                    {details && f.tracks != null ? ` · ${f.tracks} pistes` : ''} · <Lu age={f.data_age_s} />
                  </span>
                </li>
              )
            })}
          </ul>
          {flux.length > 10 ? (
            <button type="button" className="lc-btn lc-btn--ghost sv-dom-plus" onClick={() => setTout((x) => !x)}>
              {tout ? 'Réduire' : `Afficher les ${flux.length - 10} autres flux`}
            </button>
          ) : null}
        </>
      )}
    </Panneau>
  )
}

/* ─────────────────────────────── Switchs vidéo (observés, jamais conseillés) */

function SwitchsVideo({ generic, details }: { generic: Generique[]; details: boolean }) {
  const parSwitch = new Map<string, Generique[]>()
  for (const g of generic) if (g.entity_key.startsWith('hik_switch:')) parSwitch.set(g.entity_key, [...(parSwitch.get(g.entity_key) ?? []), g])
  const liste = [...parSwitch.entries()].sort(([a], [z]) => a.localeCompare(z, 'fr', { numeric: true }))
  return (
    <Panneau id="sv-vid-switchs" titre="Switchs vidéo" indice="ce qu’ils annoncent, observé seulement">
      {liste.length === 0 ? <p className="lc-hint">Aucune mesure des switchs Hikvision.</p> : (
        <ul className="sv-dom-lignes">
          {liste.map(([cle, gs], i) => {
            const v = (m: string) => gs.find((g) => g.metric === m)?.value ?? null
            const stp = bool01(v('hikvision.stp_enabled'))
            const poe = bool01(v('hikvision.poe_watchdog_enabled'))
            return (
              <li key={cle} className="sv-dom-ligne">
                <span className="sv-dom-ligne__nom">Switch vidéo {i + 1}{details ? <span className="sv-dom-mono"> {cle.replace(/^hik_switch:/, '')}</span> : null}</span>
                <Lu age={agePlusAncien(gs.map((g) => g.data_age_s))} />
                <span className="sv-dom-ligne__meta">
                  Écart d’horloge {fmtDecimal(v('hikvision.clock_offset_s'), 's', 0)}
                  {stp !== null ? ` · STP ${stp ? 'activé' : 'désactivé'} (observé)` : ''}
                  {poe !== null ? ` · surveillance PoE ${poe ? 'activée' : 'désactivée'} (observée)` : ''}
                </span>
              </li>
            )
          })}
        </ul>
      )}
    </Panneau>
  )
}
